import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  useMediaQuery,
  Pagination,
  PaginationItem,
} from '@mui/material';
import { MaterialReactTable, useMaterialReactTable, MRT_ColumnDef, MRT_Cell } from 'material-react-table';
import useDebounce from 'packages/novous_web/src/hooks/useDebounce';
import searchIcon from 'packages/novous_web/src/assets/icons/Icon.svg';
import editIcon from 'packages/novous_web/src/assets/icons/edit.svg';
import downloadIcon from 'packages/novous_web/src/assets/icons/download.svg';
import deleteIcon from 'packages/novous_web/src/assets/icons/delete1.svg';
import duplicateIcon from 'packages/novous_web/src/assets/icons/Duplicate.svg';
import putOnHoldIcon from 'packages/novous_web/src/assets/icons/putOnHold.svg';
import useAxios from 'packages/novous_web/src/api/useAxios';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import AlertToast from 'packages/novous_web/src/common/components/Alert';
import eyeIcon from 'packages/novous_web/src/assets/icons/Eye.svg';
import { useNavigate } from 'react-router-dom';
interface Trip {
  tripId: string;
  tripName: string;
  group: string;
  type: string;
  status: string;
  currentState: string;
  alerts: number;
  routeCode: string;
  routeName: string;
  employeeId: string;
  driverName: string;
  driverContact: string;
  tripStartTime?: string;
  tripEndTime?: string;
  vehicleId?: string;
}

export default function TripHistoryTable({
  vehicleRegistrationNumber,
}: {
  vehicleRegistrationNumber: string;
}) {
  const { user } = useAuth();
  const isSmallScreen = useMediaQuery('(max-width:850px)');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 200);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [toast, setToast] = useState({ open: false, message: '', type: 'error' });
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [filteredData, setFilteredData] = useState<Trip[]>([]);
  const { data, error, loading, fetchData } = useAxios<any>(
    API_ENDPOINTS.directories.VEHICLE_TRIP_RUNNING_HISTORY,
    'POST',
  );
  const navigate = useNavigate();
  const handleEyeClick = (e: React.MouseEvent, trip: Trip) => {
    e.stopPropagation();

    const params = new URLSearchParams();
    params.set('tripId', trip.tripId);

    const stateData = {
      vehicleId: trip.vehicleId, // ya trip.vehicleId
    };

    if (trip.type === 'Ignition Based') {
      navigate(`/trips/ignition/details?${params.toString()}`, { state: stateData });
    } else {
      setTripData([trip]);
      navigate(`../../../trips/details?${params.toString()}`, { state: stateData });
    }
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [paginatedArr, setPaginatedArr] = useState([null]);
  // const totalPages = useRef(paginatedArr.length);
  const totalPages = useMemo(() => {
    return Math.max(paginatedArr.length, 1);
  }, [paginatedArr]);

  const [tripData, setTripData] = useState<Trip[]>([]);

  // useEffect(()=>{
  //   totalPages.current = paginatedArr.length;
  // },[paginatedArr])

  function formatDateTime(dateStr: any, timeStr: any) {
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    // Combine date and time into a single Date object
    const dateTime = new Date(`${dateStr}T${timeStr}`);

    // Get day with ordinal suffix (1st, 2nd, 3rd, 4th, etc.)
    const day = dateTime.getDate();
    let dayWithSuffix;
    if (day > 3 && day < 21) dayWithSuffix = day + 'th';
    else {
      switch (day % 10) {
        case 1:
          dayWithSuffix = day + 'st';
          break;
        case 2:
          dayWithSuffix = day + 'nd';
          break;
        case 3:
          dayWithSuffix = day + 'rd';
          break;
        default:
          dayWithSuffix = day + 'th';
      }
    }

    // Format time in 12-hour format with AM/PM
    let hours = dateTime.getHours();
    const minutes = dateTime.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // Convert 0 to 12
    const formattedTime = `${hours}:${minutes.toString().padStart(2, '0')} ${ampm}`;

    // Get month name
    const monthName = months[dateTime.getMonth()];

    return `${dayWithSuffix} ${monthName} ${formattedTime}`;
  }
  useEffect(() => {
    setCurrentPage(1);
    setPaginatedArr([null]);
    fetchData({
      fleetId: user?.customer_id,
      lastEvaluatedKey: null,
      vehicleId: vehicleRegistrationNumber,
    });
  }, [user?.customer_id, vehicleRegistrationNumber]);

  useEffect(() => {
    if (error) {
      setToast({
        open: true,
        message: error || 'An error occurred while fetching trip data.',
        type: 'error',
      });
    }
  }, [error]);

  useEffect(() => {
    if (data?.data) {
      const trips: Trip[] = data?.data?.arrayObjectList?.map((trip: any) => ({
        tripId: trip?.tripId,
        tripName: trip?.tripName,
        group: trip?.groupName,
        type: trip?.tripIsIgnitionBased ? 'Ignition Based' : 'User Created',
        status: trip?.tripStatus,
        currentState: trip?.currentState || 'NA',
        alerts: trip?.totalAlerts || 0,
        routeCode: trip?.routeCode || 'NA',
        routeName: trip?.routeName || 'NA',
        employeeId: trip?.employeeId || 'NA',
        driverName: trip?.driverName || 'NA',
        driverContact: trip?.driverContact || 'NA',
        tripStartTime: formatDateTime(trip?.departureDate, trip?.departureTime),
        tripEndTime: formatDateTime(trip?.arrivalDate, trip?.arrivalTime),
        distanceTravelled: trip?.distanceTravelled || 'NA',
        deviceId: trip?.deviceId || 'NA',
        vehicleId: trip?.vehicleId || 'NA',
      }));
      if (data?.data?.lastEvaluatedKey) {
        if (paginatedArr.length === currentPage) {
          setPaginatedArr((prev) => [...prev, data.data.lastEvaluatedKey]);
        }
      }
      setTripData(trips);
    } else if (data?.message == 'Vehicle trip history fetched successfully' && !data?.data) {
      setToast({
        open: true,
        message: 'No trip history found for this vehicle.',
        type: 'info',
      });
      setTripData([]);
    }
  }, [data]);

  const viewMenuData = [
    { name: 'Edit Trip', icon: editIcon, click: () => {} },
    { name: 'Duplicate Trip', icon: duplicateIcon, click: () => {} },
    { name: 'Download Trip', icon: downloadIcon, click: () => {} },
    { name: 'Put on Hold', icon: putOnHoldIcon, click: () => {} },
    { name: 'Delete Trip', icon: deleteIcon, click: () => {} },
  ];

  const handleOpenMenu = (event: React.MouseEvent<HTMLSpanElement>, trip: Trip) => {
    setAnchorEl(event.currentTarget);
    setSelectedTrip(trip);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (debouncedSearchQuery) {
      const filteredDataArr = tripData.filter(
        (trip: any) =>
          (trip?.tripId && trip.tripId.toLowerCase().includes(debouncedSearchQuery.toLowerCase())) ||
          (trip?.tripName && trip.tripName.toLowerCase().includes(debouncedSearchQuery.toLowerCase())) ||
          (trip?.driverName && trip.driverName.toLowerCase().includes(debouncedSearchQuery.toLowerCase())),
      );

      console.log(filteredDataArr, 'filteredDataArr');
      //setCurrentPage(1);
      // totalPages= totalPages > 1 ? Math.ceil(filteredDataArr.length / 10) : 1;
      setFilteredData(filteredDataArr);
    } else {
      setFilteredData(tripData);
    }
    //setCurrentPage(page);
  }, [debouncedSearchQuery, tripData, searchQuery]);

  const columns: MRT_ColumnDef<Trip>[] = useMemo(
    () => [
      {
        accessorKey: 'tripId',
        header: 'Trip ID',
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={400}>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'deviceId',
        header: 'Device ID',
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={400}>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'tripName',
        header: 'Trip Name',
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={400}>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'tripStartTime',
        header: 'Trip Start Time',
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={400} sx={{ width: 240, fontSize: 14 }}>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'tripEndTime',
        header: 'Trip End Time',
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={400} sx={{ width: 240, fontSize: 14 }}>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'distanceTravelled',
        header: 'Distance Covered',
        enableColumnActions: false,
        Cell: ({ cell }) => {
          const value = cell.getValue<number>();

          //const isValidNumber = typeof value === 'number' && !isNaN(value);
          const formattedValue =
            value !== 0 && typeof value === 'number' && !isNaN(value)
              ? `${Math.round(value * 100) / 100} km`
              : 'NA';

          return (
            <Typography variant="body2" fontWeight={400}>
              {formattedValue || 0}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'group',
        header: 'Group',
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={400}>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'type',
        header: 'Type',
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            fontWeight={400}
            sx={{
              width: 130,
              height: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              borderRadius: '8px',
              padding: '4px 8px',
              backgroundColor:
                cell.getValue<string>() === 'Ignition Based'
                  ? '#E0ECFD'
                  : cell.getValue<string>() === 'User Created'
                  ? '#D9EDE6'
                  : 'transparent',
              color: cell.getValue<string>() === 'Ignition Based' ? '#2F80ED' : '#219653',
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: '16px',
            }}
          >
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            fontWeight={400}
            sx={{
              width: 110,
              height: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              borderRadius: '8px',
              padding: '4px 8px',
              backgroundColor:
                cell.getValue<string>() === 'COMPLETED'
                  ? '#E0ECFD'
                  : cell.getValue<string>() === 'PENDING'
                  ? '#D9EDE6'
                  : 'transparent',
              color: cell.getValue<string>() === 'COMPLETED' ? '#2F80ED' : '#219653',
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: '16px',
            }}
          >
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'currentState',
        header: 'Current State',
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            fontWeight={400}
            color={cell.getValue<string>() === 'NA' ? 'text.secondary' : 'warning.main'}
          >
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'alerts',
        header: '# Alerts',
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={400}>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'routeCode',
        header: 'Route Code',
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={400}>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'routeName',
        header: 'Route Name',
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={400}>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'employeeId',
        header: 'Employee ID',
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={400}>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'driverName',
        header: 'Driver Name',
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={400}>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'driverContact',
        header: "Driver's Contact",
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={400}>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        enableColumnActions: false,
        Cell: ({ row }: { row: MRT_Cell<Trip>['row'] }) => (
          <Typography
            variant="body2"
            fontWeight={700}
            color="primary"
            sx={{ cursor: 'pointer' }}
            onClick={(e) => handleEyeClick(e, row.original)}
          >
            <img src={eyeIcon} alt="View" width={16} height={16} />
          </Typography>
        ),
      },
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data: debouncedSearchQuery ? filteredData : tripData,
    enablePagination: false,
    enableColumnFilters: false,
    enableSorting: false,
    enableGlobalFilter: false,
    enableDensityToggle: false,
    enableHiding: false,
    enableBottomToolbar: false,
    enableTopToolbar: false,
    enableFullScreenToggle: false,
    enableColumnResizing: false,
    enableToolbarInternalActions: false,
    initialState: {
      columnPinning: {
        left: ['tripId', 'tripName'],
        right: ['actions'],
      },
    },
    muiTableBodyProps: {
      sx: {
        padding: 0,
        margin: 0,
      },
    },
  });
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    if (page !== 1) {
      fetchData({
        fleetId: user?.customer_id,
        lastEvaluatedKey: paginatedArr[page - 1],
        vehicleId: vehicleRegistrationNumber,
      });
    } else {
      fetchData({ fleetId: user?.customer_id, vehicleId: vehicleRegistrationNumber, lastEvaluatedKey: null });
    }
    setCurrentPage(page);
  };
  return (
    <>
      <Paper sx={{ p: 2, mt: 2, width: '100%' }}>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search trips..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <img src={searchIcon} alt="Search" width={16} height={16} />
                </InputAdornment>
              ),
              sx: { borderRadius: '8px', height: '48px' },
            }}
            sx={{ maxWidth: '400px' }}
          />
        </Box>
        <MaterialReactTable table={table} />
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            shape="rounded"
            color="primary"
            siblingCount={0}
            boundaryCount={0}
            renderItem={(item) => {
              if (item.type === 'page' && item.page !== null && item.page > currentPage) {
                return null;
              }
              return <PaginationItem {...item} />;
            }}
          />
        </Box>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
          {viewMenuData.map((item, index) => (
            <MenuItem key={index} onClick={item.click}>
              <div className="flex gap-2 mt-1">
                <img src={item.icon} alt={item.name} style={{ marginRight: 8, width: 20, height: 20 }} />
                <Typography variant="body2" fontWeight={600}>
                  {item.name}
                </Typography>
              </div>
            </MenuItem>
          ))}
        </Menu>
      </Paper>
      <AlertToast
        open={toast.open}
        message={toast.message}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        type={toast.type as any}
      />
    </>
  );
}
